import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Helpers from "../../../services/Helpers";
import { Link, useHistory } from "react-router-dom";
import Constants from "../../../services/Constants";
import {useFeatureFlag} from "../../../hooks/useFeatureFlag";
import {
  canManageStarterTemplates,
  isStarterPlanCopy,
  isStarterTemplatePlan,
  MAGIC_MEAL_PLANNER_STARTER_PLANS_FEATURE_FLAG,
  shouldShowStarterBadge,
  starterBadgeLabel,
  starterTemplateDateValue,
} from "../magicMealPlanHelpers";

import {starterPlanSearch} from "../magicMealPlanUrlHelpers";

const DEFAULT_STARTER_PLAN_ATTRIBUTION = "Plan by Team Dirty";
const starterAttribution = value => typeof value === "string" && value.trim() ? value : DEFAULT_STARTER_PLAN_ATTRIBUTION;
const CUSTOM_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export default function MagicMealPlanPreview(props) {
  let history = useHistory();
  const { assetUrl, isNotEmpty, makeUrlLookGood } = Helpers();
  const { MP_NAME_CAP: mpNameCap, MP_DESCRIPTION_CAP: mpDescriptionCap } = Constants();
  const starterPlansEnabled = useFeatureFlag(MAGIC_MEAL_PLANNER_STARTER_PLANS_FEATURE_FLAG);
  const canEditStarterTemplates = canManageStarterTemplates(props.member);
  const isStarterTemplatePreview = isStarterTemplatePlan(props.mp) && !canEditStarterTemplates;
  const isRecipeClubOnly = Boolean(props.member?.hasRV) && !Boolean(props.member?.hasMP);
  const [name, setName] = useState(props.mp.name || "");
  const [description, setDescription] = useState(props.mp.description || "");
  const [storageId, setStorageId] = useState(props.mp.storageId ?? null);
  const [persistedPhoto, setPersistedPhoto] = useState(props.mp.photo || "");
  const [starterTemplate, setStarterTemplate] = useState(Boolean(props.mp.starterTemplate));
  const [starterPlanAttribution, setStarterPlanAttribution] = useState(starterAttribution(props.mp.starterPlanAttribution));
  const [starterTemplateKey, setStarterTemplateKey] = useState(props.mp.starterTemplateKey || "");
  const [starterTemplatePublishedDate, setStarterTemplatePublishedDate] = useState(starterTemplateDateValue(props.mp.starterTemplatePublishedAt));
  const [showForm, setShowForm] = useState(false);
  const [working, setWorking] = useState(false);
  const [addingStarterPlan, setAddingStarterPlan] = useState(false);
  const [pendingCustomImage, setPendingCustomImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formError, setFormError] = useState("");
  const previewUrlRef = useRef(null);
  const fileInputRef = useRef(null);
  const addingStarterPlanRef = useRef(false);

  useEffect(() => {
    setPersistedPhoto(props.mp.photo || "");
    setName(props.mp.name || "");
    setDescription(props.mp.description || "");
    setStorageId(props.mp.storageId ?? null);
    setStarterTemplate(Boolean(props.mp.starterTemplate));
    setStarterTemplateKey(props.mp.starterTemplateKey || "");
    setStarterPlanAttribution(starterAttribution(props.mp.starterPlanAttribution));
    setStarterTemplatePublishedDate(starterTemplateDateValue(props.mp.starterTemplatePublishedAt));
  }, [props.mp]);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  function revokePreviewUrl() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }

  function clearPendingCustomImage() {
    revokePreviewUrl();
    setPendingCustomImage(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function resetFormFromPersistedPlan() {
    setName(props.mp.name || "");
    setDescription(props.mp.description || "");
    setStorageId(props.mp.storageId ?? null);
    setPersistedPhoto(props.mp.photo || "");
    setStarterTemplate(Boolean(props.mp.starterTemplate));
    setStarterTemplateKey(props.mp.starterTemplateKey || "");
    setStarterPlanAttribution(starterAttribution(props.mp.starterPlanAttribution));
    setStarterTemplatePublishedDate(starterTemplateDateValue(props.mp.starterTemplatePublishedAt));
  }

  function customImageValidationError(file) {
    if (!file) {
      return "Choose a JPEG or PNG image to upload.";
    }
    if (file.size > CUSTOM_IMAGE_MAX_BYTES) {
      return "The image must be 5 MiB or smaller.";
    }
    const extensionMatch = file.name.toLowerCase().match(/\.([^.]+)$/);
    const extension = extensionMatch ? extensionMatch[1] : "";
    const jpeg = (extension === "jpg" || extension === "jpeg") && file.type.toLowerCase() === "image/jpeg";
    const png = extension === "png" && file.type.toLowerCase() === "image/png";
    return jpeg || png ? "" : "Choose a JPEG or PNG whose file extension and content type agree.";
  }

  function handleCustomImageChange(event) {
    const file = event.target.files?.[0] || null;
    revokePreviewUrl();
    setPendingCustomImage(null);
    setPreviewUrl(null);
    const validationError = customImageValidationError(file);
    if (validationError) {
      setFormError(validationError);
      event.target.value = "";
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    previewUrlRef.current = objectUrl;
    setStorageId(props.mp.storageId ?? null);
    setPendingCustomImage(file);
    setPreviewUrl(objectUrl);
    setFormError("");
  }

  function handleStandardImageSelection(id) {
    clearPendingCustomImage();
    setStorageId(id);
    setFormError("");
  }

  function handleStarterTemplateChange(event) {
    const checked = event.target.checked;
    setStarterTemplate(checked);
    if (!checked) {
      clearPendingCustomImage();
      setFormError("");
    }
  }

  function failureMessage(error, fallback) {
    const apiMessage = error?.response?.data?.error;
    return typeof apiMessage === "string" && apiMessage.trim() ? apiMessage : fallback;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setWorking(true);
    setFormError("");
    const imageToUpload = pendingCustomImage;
    const updatePayload = {
      id: props.mp.id,
      name: name,
      description: description,
      storageId: storageId,
    };
    if (canEditStarterTemplates) {
      updatePayload.starterTemplate = starterTemplate;
      updatePayload.starterPlanAttribution = starterAttribution(starterPlanAttribution);
      updatePayload.starterTemplateKey = starterTemplateKey;
      updatePayload.starterTemplatePublishedAt = starterTemplate && starterTemplatePublishedDate ? `${starterTemplatePublishedDate}T00:00:00Z` : null;
    }
    let metadataSaved = false;
    try {
      await props.mmpStore.update(updatePayload);
      metadataSaved = true;
      if (imageToUpload) {
        const uploadedImage = await props.mmpStore.uploadCustomImage(props.mp.id, imageToUpload);
        setStorageId(uploadedImage.storageId);
        setPersistedPhoto(uploadedImage.photo);
      }
      clearPendingCustomImage();
      setShowForm(false);
    } catch (error) {
      clearPendingCustomImage();
      setStorageId(props.mp.storageId ?? null);
      setPersistedPhoto(props.mp.photo || "");
      if (metadataSaved && imageToUpload) {
        const imageFailure = failureMessage(error, "The image could not be saved. Your previous image is still in place. Please choose the file and try again.");
        setFormError(`Your meal plan changes were saved, but ${imageFailure.charAt(0).toLowerCase()}${imageFailure.slice(1)}`);
      } else {
        setFormError(failureMessage(error, "The meal plan changes could not be saved. Please try again."));
      }
    } finally {
      setWorking(false);
    }
  }

  const planUrl = `/meal-plans/meal-planner/${makeUrlLookGood(props.mp.id, props.mp.name)}${starterPlanSearch("?v=plan", props.mp)}`;
  const hideOrDeleteLabel = isStarterPlanCopy(props.mp) ? "Hide" : "Delete";

  const cancelButton = working ? (
    <button type="button" className="btn btn-light ms-2 mb-2 me-2" disabled={true}>
      Cancel
    </button>
  ) : (
    <button
      type="button"
      className="btn btn-light ms-2 mb-2 me-2"
      onClick={() => {
        clearPendingCustomImage();
        setFormError("");
        setShowForm(false);
        resetFormFromPersistedPlan();
      }}
    >
      Cancel
    </button>
  );

  const submitButton = working ? (
    <button type="button" className="btn btn-primary ms-2" disabled={true}>
      <span className="spinner-border spinner-border-sm ms-2 mb-2" />
      <span className="ms-1">{pendingCustomImage ? "Saving and uploading..." : "Saving..."}</span>
    </button>
  ) : (
    <button type="submit" className="btn btn-secondary mb-2">
      Save Changes
    </button>
  );

  const images = props.mmpStore.state.availableImages.map((i) => (
    <li key={i.id}>
      <button type="button" className="dropdown-item" disabled={working} onClick={() => handleStandardImageSelection(i.id)}>
        <img src={assetUrl(i.storageKey)} width="70px" alt={i.name} />
      </button>
    </li>
  ));

  function selectedImage() {
    if (previewUrl) {
      return (
        <>
          <img src={previewUrl} width="100px" alt="Selected custom cover preview" />
          <div className="small text-muted mt-2">Custom image preview — not uploaded yet</div>
        </>
      );
    }
    if (isNotEmpty(storageId)) {
      const image = props.mmpStore.state.availableImages.find((i) => i.id === storageId);
      if (image) {
        return <img src={assetUrl(image.storageKey)} width="100px" alt={image.name} />;
      }
    }
    if (isNotEmpty(persistedPhoto)) {
      return <img src={assetUrl(persistedPhoto)} width="100px" alt={`${props.mp.name} cover`} />;
    }
    return null;
  }

  function starterBadge() {
    if (!shouldShowStarterBadge({plan: props.mp, starterPlansEnabled, isAdmin: canEditStarterTemplates})) {
      return null;
    }
    return (
      <span className="badge rounded-pill text-bg-light border border-secondary-subtle text-dark mb-2">
        {starterBadgeLabel(props.mp)}
      </span>
    );
  }

  function starterSourceLabel() {
    if (!isStarterTemplatePlan(props.mp)) {
      return null;
    }
    const planAuthor = isStarterTemplatePlan(props.mp) ? starterAttribution(props.mp.starterPlanAttribution) : null;
    if (!props.mp.starterPlanSourceName && !planAuthor) {
      return null;
    }
    return (
      <div className="small text-muted mb-2">
        {props.mp.starterPlanSourceName ? (
          <div>Built from Starter Plan: {props.mp.starterPlanSourceName}</div>
        ) : null}
        {planAuthor ? (
          <div data-testid="starter-template-attribution" style={{overflowWrap: "anywhere"}}>{planAuthor}</div>
        ) : null}
      </div>
    );
  }

  function starterCopySourceContext() {
    if (!isStarterPlanCopy(props.mp)) {
      return null;
    }
    return (
      <div data-testid="starter-copy-source-context" className="small text-muted text-start mb-3">
        {props.mp.starterPlanSourceName ? (
          <div>Built from Starter Plan: {props.mp.starterPlanSourceName}</div>
        ) : null}
        <div data-testid="starter-copy-attribution" style={{overflowWrap: "anywhere"}}>
          {starterAttribution(props.mp.starterPlanAttribution)}
        </div>
      </div>
    );
  }

  function starterPublicationLabel() {
    if (!canEditStarterTemplates || !props.mp.starterTemplate) {
      return null;
    }
    const publishedDate = starterTemplateDateValue(props.mp.starterTemplatePublishedAt);
    return (
      <div className="small text-muted mb-2">
        {publishedDate ? `Published on ${publishedDate}` : "Draft - admins only"}
      </div>
    );
  }

  function addStarterPlanButton() {
    if (!isStarterTemplatePreview) {
      return null;
    }
    return (
      <button
        type="button"
        className="btn btn-primary mt-2"
        onClick={() => {
          if (isRecipeClubOnly) {
            props.onProtectedAction();
          } else if (!addingStarterPlanRef.current) {
            addingStarterPlanRef.current = true;
            setAddingStarterPlan(true);
            props.onAddStarterPlan(props.mp.id, () => {
              addingStarterPlanRef.current = false;
              setAddingStarterPlan(false);
            });
          }
        }}
        disabled={addingStarterPlan}
      >
        {addingStarterPlan ? "Adding..." : "Add to Meal Planner"}
      </button>
    );
  }

  function cardImage() {
    const image = isNotEmpty(persistedPhoto) ? (
      <img src={assetUrl(persistedPhoto)} className="card-img-top" alt={props.mp.name} />
    ) : (
      <img src={`${process.env.PUBLIC_URL}/images/recipe-placeholder-picture.png`} className="card-img-top rounded-top" alt={props.mp.name} />
    );
    return isStarterTemplatePreview ? image : (
      <Link to={planUrl} className="card-link">
        {image}
      </Link>
    );
  }

  function cardTitle() {
    return isStarterTemplatePreview ? props.mp.name : (
      <Link to={planUrl} className="card-link">
        {props.mp.name}
      </Link>
    );
  }

  return showForm ? (
    <div className="tile col mb-4">
      <div className="card h-100 p-2 mp-saved text-center">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3" data-testid="meal-plan-name-group">
              <label className="form-label">Meal Plan Name</label>
              <input
                type="text"
                autoComplete="off"
                className="form-control"
                maxLength={mpNameCap}
                value={name}
                placeholder="Name your Meal Plan..."
                required={true}
                disabled={working}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {starterCopySourceContext()}
            <div className="mb-3">
              <div className="d-flex flex-row justify-content-center bg-light p-2 py-3 rounded text-center">
                <div>
                  {selectedImage()}
                  <div className="dropdown mt-3" id="weekly-plan-select">
                    <button className="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" disabled={working}>
                      Select Image
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                      {images}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea className="form-control" maxLength={mpDescriptionCap} value={description} disabled={working} onChange={(e) => setDescription(e.target.value)} />
            </div>
            {canEditStarterTemplates ? (
              <>
                {starterTemplate && props.mp.starterTemplateCreatorName ? (
                  <div className="small text-muted text-start mb-3">
                    Template creator: {props.mp.starterTemplateCreatorName}
                  </div>
                ) : null}
                <div className="form-check text-start mb-3">
                  <input
                    id={`starter-template-${props.mp.id}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={starterTemplate}
                    disabled={working}
                    onChange={handleStarterTemplateChange}
                  />
                  <label className="form-check-label" htmlFor={`starter-template-${props.mp.id}`}>
                    Mark as starter template
                  </label>
                  <div className="small text-muted mt-1">
                    Do not check this box without approval from Molly.
                  </div>
                </div>
                {starterTemplate ? (
                  <div className="mb-3 text-start">
                    <label className="form-label" htmlFor={`starter-attribution-${props.mp.id}`}>Plan by</label>
                    <input
                      id={`starter-attribution-${props.mp.id}`}
                      type="text"
                      className="form-control"
                      maxLength={256}
                      value={starterPlanAttribution}
                      disabled={working}
                      aria-describedby={`starter-attribution-help-${props.mp.id}`}
                      onChange={event => setStarterPlanAttribution(event.target.value)}
                    />
                    <small id={`starter-attribution-help-${props.mp.id}`} className="text-muted">
                      Write “Plan by Team Dirty” or, if this is a collab, do “Plan by Name” or “Plan sponsored by Carrots.”
                    </small>
                  </div>
                ) : null}
                {starterTemplate ? (
                  <div className="mb-3 text-start">
                    <label className="form-label" htmlFor={`starter-custom-image-${props.mp.id}`}>
                      Custom Starter Template Image
                    </label>
                    <input
                      id={`starter-custom-image-${props.mp.id}`}
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      disabled={working}
                      onChange={handleCustomImageChange}
                    />
                    <small className="text-muted">JPEG or PNG, up to 5 MiB. Images wider than 1600 pixels are resized when uploaded.</small>
                  </div>
                ) : null}
                <div className="mb-3 text-start">
                  <label className="form-label">Starter Template Key</label>
                  <input
                    type="text"
                    autoComplete="off"
                    className="form-control"
                    value={starterTemplateKey}
                    placeholder="standard or batch-focused"
                    disabled={working || !starterTemplate}
                    onChange={(e) => setStarterTemplateKey(e.target.value)}
                  />
                  <small className="text-muted">Keep this key stable after rollout so existing copies do not reprovision.</small>
                </div>
                <div className="mb-3 text-start">
                  <label className="form-label">Publish Date</label>
                  <input
                    type="date"
                    autoComplete="off"
                    className="form-control"
                    value={starterTemplatePublishedDate}
                    disabled={working || !starterTemplate}
                    onChange={(e) => setStarterTemplatePublishedDate(e.target.value)}
                  />
                  <small className="text-muted">Leave empty to keep this starter template visible to admins only.</small>
                </div>
              </>
            ) : null}
            {formError ? (
              <div className="alert alert-danger text-start" role="alert">
                {formError}
              </div>
            ) : null}
            {cancelButton}
            {submitButton}
          </form>
        </div>
      </div>
    </div>
  ) : (
    <div className="tile col mb-4">
      <div className="card h-100 text-center border-0 shadow-sm tile-magic bg-white">
        <div className="image-wrap">
          {cardImage()}
        </div>
        <div className="card-body p-2 bg-white rounded-bottom align-middle">
          {starterBadge()}
          <h2 className="card-title-1 py-1">
            {cardTitle()}
          </h2>
          {starterSourceLabel()}
          {starterPublicationLabel()}
          {addStarterPlanButton()}
          {isStarterTemplatePreview ? null : (
            <>
              <button type="button" className="btn btn-stacked" onClick={props.showDeleteModal}>
                <p className="fa-action fa-regular fa-circle-minus">
                  <br />
                  <span className="fa-action-text">{hideOrDeleteLabel}</span>
                </p>
              </button>
              <button className="btn btn-stacked" type="button" onClick={() => {
                clearPendingCustomImage();
                resetFormFromPersistedPlan();
                setFormError("");
                setShowForm(true);
              }}>
                <p className="fa-action far fa-square-pen">
                  <br />
                  <span className="fa-action-text">Edit</span>
                </p>
              </button>
              <button className="btn btn-stacked" type="button" onClick={() => history.push(planUrl)}>
                <p className="fa-action far fa-chevron-circle-right">
                  <br />
                  <span className="fa-action-text">View</span>
                </p>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

MagicMealPlanPreview.propTypes = {
  member: PropTypes.object,
  mmpStore: PropTypes.object,
  mp: PropTypes.object,
  onAddStarterPlan: PropTypes.func,
  onProtectedAction: PropTypes.func,
  showDeleteModal: PropTypes.func,
};
